import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import dynamic from 'next/dynamic';

import { ListIcons } from 'components/Stage/utils';
import { SellingPoints } from 'components/Stage/SellingPoints/SellingPoints';
import { LoanSelectorSkeleton } from 'components/LoanSelector/LoanSelectorSkeleton';
import { Badge } from '../StageParts/Badge';
import { PartnerPanels } from '../StageParts/PartnerPanels';
import { AppDownloadSection } from '../StageParts/AppDownloadSection';
import { expandTextMacros } from 'lib/text-macros/textMacroHelper';

import type { ISeaStageFields } from 'types/generated/contentful';
import { BlackFridayBadge } from 'components/Stage/Badges/BlackFridayBadge';
import { useDecision } from 'lib/helpers/optimizely';
import {
  ABTestKeys,
  BLACK_FRIDAY_FLAG,
  Variation,
} from 'lib/helpers/optimizely/flagConfigs';
import { getOsName } from 'lib/router/route-helper';
import { useState, useEffect } from 'react';
import { getAppRedirectionExperimentStageConfig } from 'components/ABTestVariations/CAT3820AppRedirectionV4/helpers';
import { Overlay } from 'components/ABTestVariations/CAT3820AppRedirectionV4/Overlay';

// Below-the-fold dynamic imports for better LCP
const LegalTextGoogleWithExampleDynamic = dynamic(
  () =>
    import('components/Stage/LegalTextGoogle').then((m) => ({
      default: m.LegalTextGoogleWithExample,
    })),
  { ssr: true },
);

const LoanSelector = dynamic(
  () => import('components/LoanSelector').then((mod) => mod.LoanSelector),
  {
    loading: () => <LoanSelectorSkeleton />,
    ssr: true,
  },
);

const HEADLINE_DEFAULT = 'Günstige Kredite - garantiert!';

const Headline = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: 26,
  width: '100%',
  marginBottom: 0,
  [theme.breakpoints.up('md')]: {
    lineHeight: '64px',
    fontSize: 48,
    fontWeight: 700,
    width: '90%',
  },
}));

const HeadlineGridItem = styled(Grid)(({ theme }) => ({
  padding: '0',
  height: 'auto',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    paddingLeft: '1%',
  },
}));

const DesktopBadgeWrapper = styled('div')`
  position: relative;
  top: 0px;
  right: 2px;
  display: flex;
  flex-direction: column;
  -webkit-box-align: center;
  align-items: center;
  margin: -35px 0px 0px -60px;
`;

const BadgeWrapper = styled('div')`
  margin-bottom: 8px;

  :nth-last-child(1) {
    margin: 0;
  }

  img {
    display: block;
    width: 100px;
    height: 100px;
    margin-left: 10px;
  }
`;

const HideBadgeSmallerMedium = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: 0,
    display: 'none',
  },
}));

const PartnerPanelsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: theme.spacing(2),
  height: '100px',
  width: '100%',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),

  // hide scrollbar but keep functionality
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },

  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(2),
  },
}));

const LegalTextWrapper = styled('div')(() => ({
  width: '100%',
}));

const LpContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export type SeaStageProps = ISeaStageFields;

export const SeaStage = (props: SeaStageProps) => {
  const { headline, bulletPoints, badge, partners, ctaText, additionalParams } =
    props;

  const isBadgeIncluded = badge && badge.fields;

  const isTvBannerEnabled = false;
  const isAppDownloadSectionEnabled = false; // AB Test for APP is currently off.
  const listIcon = ListIcons.CHECK_MARKS;
  const headlineWithMacros = expandTextMacros(headline);

  const [decision] = useDecision(ABTestKeys.CAT3106_Black_friday_banner);

  const showBlackFridayBadge = decision.variationKey === BLACK_FRIDAY_FLAG;
  const [isMounted, setIsMounted] = useState(false);
  const [deviceOS, setDeviceOS] = useState('unknown');

  useEffect(() => {
    setIsMounted(true);
    setDeviceOS(getOsName());
  }, []);

  // Mobile detection for AB test - deviceOS initialises to 'unknown' on SSR
  // and updates client-side to avoid hydration mismatch

  const overrideAttributes = {
    overrideAttributes: {
      deviceOS,
    },
  };

  const [decisionCAT4168AppRedirectionV5] = useDecision(
    ABTestKeys.CAT_4600_APP_REDIRECTION_V3,
    undefined,
    { ...overrideAttributes },
  );

  const appRedirectionVariation =
    decisionCAT4168AppRedirectionV5.variationKey || null;

  const appRedirectionExperimentConfig = getAppRedirectionExperimentStageConfig(
    appRedirectionVariation || '',
  );

  return (
    <Box
      sx={{
        background:
          'linear-gradient(0deg, #f7f7f7 0%, rgba(247, 247, 247, 0) 100%)',
        padding: { xs: '24px 0 16px', md: '64px 0 32px' },
      }}
    >
      <LpContainer>
        <Grid container>
          {appRedirectionVariation &&
            appRedirectionExperimentConfig.isOverlayEnabled && (
              <Overlay variation={appRedirectionVariation as Variation} />
            )}
          <HeadlineGridItem size={{ xs: isBadgeIncluded ? 8 : 12, md: 6 }}>
            <Headline variant="h1" suppressHydrationWarning>
              {headlineWithMacros || HEADLINE_DEFAULT}
            </Headline>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <SellingPoints sellingPoints={bulletPoints} listIcon={listIcon} />
              <PartnerPanelsWrapper>
                <PartnerPanels partners={partners} />
              </PartnerPanelsWrapper>
            </Box>
          </HeadlineGridItem>
          <Grid
            size={{ xs: 4 }}
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'flex-end',
            }}
          >
            {showBlackFridayBadge ? (
              <BlackFridayBadge />
            ) : (
              <>
                <Badge content={badge} />
              </>
            )}
          </Grid>
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: { xs: 'block', md: 'none' },
              textAlign: 'center',
            }}
          >
            <SellingPoints sellingPoints={bulletPoints} listIcon={listIcon} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {isAppDownloadSectionEnabled && <AppDownloadSection />}
            {isMounted && (
              <LoanSelector
                ctaText={ctaText}
                hideTvBanner={!isTvBannerEnabled}
                isCreditCardCategoryEnabled
                additionalParams={additionalParams}
                appRedirectionVariation={appRedirectionVariation}
              >
                <DesktopBadgeWrapper>
                  <HideBadgeSmallerMedium>
                    <Badge content={badge} />
                    {showBlackFridayBadge && (
                      <BadgeWrapper>
                        <BlackFridayBadge />
                      </BadgeWrapper>
                    )}
                  </HideBadgeSmallerMedium>
                </DesktopBadgeWrapper>
              </LoanSelector>
            )}
          </Grid>
          <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
            <PartnerPanelsWrapper>
              {showBlackFridayBadge && <Badge content={badge} />}
              <PartnerPanels partners={partners} />
            </PartnerPanelsWrapper>
            <LegalTextWrapper>
              <LegalTextGoogleWithExampleDynamic />
            </LegalTextWrapper>
          </Box>
        </Grid>
      </LpContainer>
    </Box>
  );
};
